/**
 * Eigenbau: Quadratischer Zustandspunkt, 7×7 — Tremors Marken sind Chips.
 *
 * Der Entwurf zeigt Zustände als kleines Quadrat mit Haarlinie, daneben das
 * Wort. 34 Stellen. Tremors `Badge` wäre ein gefüllter Chip mit Text darin —
 * eine andere Bauform, die in einer Zeile mit fester Höhe zu viel Platz nimmt
 * und die Zeile optisch in Abschnitte zerteilt.
 *
 * Die Farbe kommt **nie** als Prop, sondern immer aus `ZUSTAND`, `SCHWERE` oder
 * `STAND` in `lib/entwurf-zustaende`. Sonst wäre die Regel „Zustand ist
 * Tintendichte, nicht Farbe" eine Bitte statt einer Zusage.
 *
 * ── Barrierefreiheit ──────────────────────────────────────────────────────
 *
 * Der Punkt ist **dekorativ** (`aria-hidden`). Er darf nie die einzige Quelle
 * sein: Paket 19 verlangt „Farbe nie allein: Schweregrad trägt immer ein Wort",
 * und ein 7×7-Quadrat unterscheidet sich für Farbenblinde in vier Stufen
 * Tintendichte nicht zuverlässig. Deshalb nimmt `Zustandsmarke` darunter das
 * Wort mit — und wer nur den Punkt setzt, muss den Text daneben selbst stellen.
 */

import { cx } from "@/lib/utils";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import type { Punktbild } from "@/lib/entwurf-zustaende";

export function Zustandspunkt({
  bild,
  strich = false,
  className,
}: {
  bild: Punktbild;
  /**
   * Übersprungen: durchgestrichen, nicht eingefärbt. Der Entwurf begründet es
   * selbst — sonst wäre „nicht gelaufen" von „gelaufen" nur an der Helligkeit
   * zu erkennen.
   */
  strich?: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "relative block size-[7px] flex-none border",
        bild.fuellung,
        bild.rand,
        className,
      )}
    >
      {strich ? (
        <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-ring" />
      ) : null}
    </span>
  );
}

/**
 * Punkt und Wort zusammen — die Form, in der der Entwurf Zustände fast immer
 * zeigt. Wer diese Marke benutzt, kann die Farbregel nicht versehentlich
 * brechen.
 */
export function Zustandsmarke({
  bild,
  label,
  ton,
  strich = false,
  className,
}: {
  bild: Punktbild;
  label: string;
  /** Klassenname für den Text, aus `STAND`/`SCHWERE`. Vorgabe: leise. */
  ton?: string;
  strich?: boolean;
  className?: string;
}) {
  return (
    <span className={cx("flex flex-none items-center gap-2", className)}>
      <Zustandspunkt bild={bild} strich={strich} />
      <Mikrolabel className={ton ?? "text-tinte3"}>{label}</Mikrolabel>
    </span>
  );
}
